import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SignatureCanvas from "react-signature-canvas";
import { toTitleCase } from "../../utils/helper/routeAuditUi";

const getSignatureImageSrc = (signature) => {
	if (!signature || typeof signature !== "object") {
		return "";
	}

	return (
		signature.image_url ||
		signature.imageUrl ||
		signature.url ||
		signature.path ||
		""
	);
};

export const HandoverSignaturePreview = ({ signature, title = "Signature", className = "" }) => {
	const imageSrc = useMemo(() => getSignatureImageSrc(signature), [signature]);

	if (!imageSrc) {
		return null;
	}

	return (
		<div className={`rounded-2xl border border-slate-200 bg-white/80 p-3 ${className}`.trim()}>
			<p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</p>
			{signature?.user_name ? (
				<p className="mt-1 text-xs text-slate-600">{toTitleCase(signature.user_name)}</p>
			) : null}
			<div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white">
				<img src={imageSrc} alt={title} className="h-28 w-full object-contain bg-white" />
			</div>
		</div>
	);
};

export const HandoverSignaturePad = ({
	title = "",
	description = "",
	value = "",
	onChange,
	className = "",
	compact = false,
	canvasStyle,
}) => {
	const sigRef = useRef(null);
	const lastAppliedValueRef = useRef("");

	useEffect(() => {
		if (!sigRef.current) {
			return;
		}

		if (!value) {
			if (!sigRef.current.isEmpty()) {
				sigRef.current.clear();
			}
			lastAppliedValueRef.current = "";
			return;
		}

		if (value === lastAppliedValueRef.current || !sigRef.current.isEmpty()) {
			return;
		}

		try {
			sigRef.current.fromDataURL(value);
			lastAppliedValueRef.current = value;
		} catch (_) {}
	}, [value]);

	const handleClear = () => {
		sigRef.current?.clear();
		lastAppliedValueRef.current = "";
		onChange?.("");
	};

	const handleEnd = () => {
		const nextValue = sigRef.current?.isEmpty() ? "" : sigRef.current?.toDataURL();
		lastAppliedValueRef.current = nextValue || "";
		onChange?.(nextValue || "");
	};

	return (
		<div className={`rounded-2xl border border-slate-200 bg-white ${compact ? "p-2" : "p-3"} ${className}`.trim()}>
			{title ? (
				<p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</p>
			) : null}
			{description ? (
				<p className={`${title ? "mt-1" : ""} text-xs text-slate-600`.trim()}>{description}</p>
			) : null}
			<div className={`${title || description ? "mt-2" : ""} rounded-2xl border border-slate-200 bg-slate-50 ${compact ? "p-2" : "p-3"}`}>
				<SignatureCanvas
					ref={sigRef}
					penColor="black"
					clearOnResize={false}
					canvasProps={{ className: "signature", style: canvasStyle }}
					onEnd={handleEnd}
				/>
				<div className={`${compact ? "mt-2" : "mt-3"} flex justify-end`}>
					<button
						type="button"
						onClick={handleClear}
						className={`rounded-xl border border-slate-300 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100 ${compact ? "px-2.5 py-1.5" : "px-3 py-2"}`}
					>
						Clear
					</button>
				</div>
			</div>
		</div>
	);
};

export const HandoverSignatureModal = ({
	isOpen,
	title,
	description,
	confirmLabel = "Save Signature",
	cancelLabel = "Cancel",
	confirmButtonClassName = "bg-green-600 hover:bg-green-500",
	onCancel,
	onConfirm,
	loading = false,
	errorMessage = "",
}) => {
	const sigRef = useRef(null);
	const [signatureValue, setSignatureValue] = useState("");

	useEffect(() => {
		if (!isOpen) {
			setSignatureValue("");
			sigRef.current?.clear();
		}
	}, [isOpen]);

	const handleClear = () => {
		sigRef.current?.clear();
		setSignatureValue("");
	};

	const handleEnd = () => {
		const nextValue = sigRef.current?.isEmpty() ? "" : sigRef.current?.toDataURL();
		setSignatureValue(nextValue || "");
	};

	const handleConfirm = () => {
		if (!signatureValue || loading) {
			return;
		}

		onConfirm?.(signatureValue);
	};

	if (!isOpen) {
		return null;
	}

	if (typeof document === "undefined" || !document.body) {
		return null;
	}

	const stopEvent = (event) => {
		event.stopPropagation();
	};

	const stopScrollEvent = (event) => {
		event.stopPropagation();
		if (event.cancelable) {
			event.preventDefault();
		}
	};

	return createPortal(
		<div
			className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/60 p-4"
			onMouseDown={stopEvent}
			onTouchStart={stopEvent}
			onTouchMove={stopScrollEvent}
			onTouchEnd={stopEvent}
			onWheel={stopScrollEvent}
		>
			<div
				className="w-full max-w-xl overflow-hidden rounded-[28px] bg-white shadow-[0_30px_80px_rgba(15,23,42,0.35)]"
				onMouseDown={stopEvent}
				onTouchStart={stopEvent}
				onTouchMove={stopScrollEvent}
				onTouchEnd={stopEvent}
				onWheel={stopScrollEvent}
			>
				<div className="border-b border-slate-200 px-5 py-4">
					<h3 className="text-lg font-semibold text-slate-900">{title}</h3>
					{description ? (
						<p className="mt-1 text-sm text-slate-600">{description}</p>
					) : null}
				</div>

				<div className="px-5 py-4">
					<div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
						<SignatureCanvas
							ref={sigRef}
							penColor="black"
							clearOnResize={false}
							canvasProps={{ className: "signature" }}
							onEnd={handleEnd}
						/>
						<div className="mt-3 flex justify-end">
							<button
								type="button"
								onClick={handleClear}
								className="rounded-xl border border-slate-300 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100"
							>
								Clear
							</button>
						</div>
					</div>
				</div>

				{errorMessage ? (
					<div className="mx-5 mb-1 rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
						{errorMessage}
					</div>
				) : null}

				<div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 px-5 py-4">
					{!loading ? (
						<button
							type="button"
							onClick={onCancel}
							className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
						>
							{cancelLabel}
						</button>
					) : null}
					<button
						type="button"
						onClick={handleConfirm}
						disabled={!signatureValue || loading}
						className={`rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${confirmButtonClassName}`.trim()}
					>
						{loading ? "Saving..." : confirmLabel}
					</button>
				</div>
			</div>
		</div>,
		document.body
	);
};
